import { useState, useEffect, useRef } from "react";
import { SignUpButton, useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Button from "./ui/Button";

const STORAGE_KEY = "whobrew_desired_handle";

function sanitize(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

interface Props {
  origin: string;
}

export default function HeroHandlePicker({ origin }: Props) {
  const { isSignedIn } = useUser();
  const navigate = useNavigate();
  const [handle, setHandle] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) setHandle(sanitize(stored));
    } catch {}
  }, []);

  const sanitized = sanitize(handle);
  const valid = sanitized.length >= 2 && sanitized.length <= 32;
  const previewUrl = valid ? `${origin}/card/${sanitized}` : `${origin}/card/your-handle`;

  const persist = () => {
    if (!valid) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, sanitized);
    } catch {}
  };

  const handleClaim = () => {
    persist();
    if (isSignedIn) navigate("/my-card");
  };

  return (
    <div className="border border-latte-300 dark:border-latte-800 rounded-lg bg-cream-50 dark:bg-coffee-800 p-3 sm:p-4">
      <label
        htmlFor="handle-picker"
        className="block font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 mb-2 px-1"
      >
        Pick your handle
      </label>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex items-center flex-1 bg-cream-100 dark:bg-coffee-900 border border-latte-300 dark:border-latte-800 rounded px-3 py-3 focus-within:border-coffee-900 dark:focus-within:border-cream-50 transition-colors">
          <span className="text-coffee-600 dark:text-cream-200 text-sm font-mono select-none">
            whobrew.us/card/
          </span>
          <input
            id="handle-picker"
            ref={inputRef}
            type="text"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="yourname"
            autoComplete="off"
            spellCheck={false}
            className="flex-1 min-w-0 bg-transparent text-coffee-900 dark:text-cream-50 text-sm font-mono placeholder-coffee-400 dark:placeholder-latte-500 outline-none"
          />
        </div>
        {isSignedIn ? (
          <Button onClick={handleClaim} size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>
            Open my card
          </Button>
        ) : (
          <SignUpButton mode="modal">
            <Button onClick={persist} size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>
              Claim my link
            </Button>
          </SignUpButton>
        )}
      </div>
      {valid && (
        <p className="text-caption text-coffee-600 dark:text-cream-200 mt-2 px-1">
          Yours the moment you sign up:{" "}
          <span className="font-mono text-coffee-900 dark:text-cream-50">{previewUrl}</span>
        </p>
      )}
    </div>
  );
}

export { STORAGE_KEY as DESIRED_HANDLE_KEY, sanitize as sanitizeHandle };
